
import { Permission } from "../types";
import { PERMISSIONS_STORAGE_KEY } from "../defaultPermissions";
import { deepClone } from "../utils/helpers";

/**
 * Save permissions to localStorage with robust error handling and verification
 */
export const savePermissionsToStorage = (permissions: Permission[]): boolean => {
  try {
    console.log("[PermissionsStorage] Saving permissions to localStorage, count:", permissions?.length || 0);
    
    // Ensure we have a valid array
    if (!Array.isArray(permissions)) {
      console.error("[PermissionsStorage] Cannot save permissions: not an array");
      return false;
    }
    
    // Deep clone permissions before saving to break references
    const permissionsToSave = deepClone(permissions);
    
    // Save the permissions
    const permissionsJson = JSON.stringify(permissionsToSave);
    console.log(`[PermissionsStorage] JSON string length: ${permissionsJson.length} chars`);
    
    localStorage.setItem(PERMISSIONS_STORAGE_KEY, permissionsJson);
    console.log("[PermissionsStorage] Write to localStorage completed");
    
    // Verify the save operation was successful
    try {
      const storedJson = localStorage.getItem(PERMISSIONS_STORAGE_KEY);
      if (!storedJson) {
        console.error("[PermissionsStorage] Verification failed: no data found after save");
        return false;
      }
      
      // Parse and compare key counts as a basic validation
      const storedData = JSON.parse(storedJson);
      if (!Array.isArray(storedData) || storedData.length !== permissionsToSave.length) {
        console.error("[PermissionsStorage] Verification failed: stored data does not match expected format or count");
        console.log(`[PermissionsStorage] Expected ${permissionsToSave.length} items, got ${storedData?.length || 0}`);
        return false;
      }
      
      console.log("[PermissionsStorage] Verification successful: data saved correctly to localStorage");
    } catch (verifyError) {
      console.error("[PermissionsStorage] Error verifying saved permissions:", verifyError);
      return false;
    }
    
    return true;
  } catch (error) {
    console.error("[PermissionsStorage] Error saving permissions to storage:", error);
    return false;
  }
};

/**
 * Debug function to log current localStorage content
 */
export const debugLogStorageContent = (): void => {
  try {
    const content = localStorage.getItem(PERMISSIONS_STORAGE_KEY);
    console.log("[PermissionsStorage] Current localStorage content:");
    console.log(content ? `Found data (${content.length} chars)` : "No data found");
    
    if (content) {
      try {
        const parsed = JSON.parse(content);
        console.log(`Parsed ${parsed?.length || 0} permission items`);
      } catch (e) {
        console.error("Failed to parse storage content:", e);
      }
    }
  } catch (e) {
    console.error("Error accessing localStorage:", e);
  }
};
